import { useEffect } from "react";
import { getToken, onMessage } from "firebase/messaging";
import { getFirebaseMessaging } from "../services/firebase";
import { patch } from "../services/apiClient";
import { getAuthToken } from "../utils/auth";

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

/**
 * Initialises Firebase Web Push for the admin panel.
 *
 * - Requests browser notification permission once.
 * - Registers the FCM service worker and obtains a device token.
 * - Sends the token to the backend (only when the admin is logged in).
 * - Foreground messages: plays notification.mp3 + shows a browser Notification.
 * - Background messages: handled by firebase-messaging-sw.js (OS default sound).
 */
export function useFcmMessages() {
  useEffect(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;

    if (!VAPID_KEY) {
      console.warn("[FCM] VITE_FIREBASE_VAPID_KEY not set — web push disabled");
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let isCancelled = false;

    async function init() {
      try {
        const messaging = await getFirebaseMessaging();
        if (!messaging || isCancelled) return;

        const permission = await Notification.requestPermission();
        if (permission !== "granted" || isCancelled) return;

        const swReg = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
        );
        if (isCancelled) return;

        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swReg,
        });

        if (token && !isCancelled) {
          console.log("[FCM Token]", token);
          // Only register with backend when admin is authenticated
          if (getAuthToken()) {
            await patch("/notifications/fcm-token", { fcmToken: token }).catch(
              () => null,
            );
          }
        }

        if (isCancelled) return;

        unsubscribe = onMessage(messaging, (payload) => {
          console.log(
            "[FCM Foreground]",
            payload.notification?.title,
            payload.data,
          );

          // Play custom notification sound
          const audio = new Audio("/notification.mp3");
          audio.play().catch(() => null);

          // Show browser notification banner
          if (payload.notification) {
            new Notification(payload.notification.title ?? "Ammayum Kunjum", {
              body: payload.notification.body ?? "",
              icon: "/favicon.png",
            });
          }
        });
      } catch (err) {
        console.warn("[FCM] Setup failed:", err);
      }
    }

    init();
    return () => {
      isCancelled = true;
      unsubscribe?.();
    };
  }, []);
}
